import type { B2bOrderStatus, B2bScheme, OrderLineInput, OrderQuote, SchemeEntitlement } from '@api/b2b';
import { Button, RingProgress } from '@mantine/core';
import { ButtonVariant } from '@providers/Mantine/mantineTheme';
import React, { useState } from 'react';

import styles from './b2b.module.css';
import { describeScheme, freeChosen, freeSchemeProgress, money, ORDER_STEPS, sumLines, units } from './utils';

/* Small pieces shared by the brand's B2B screens and the customer portal. */

type Tone = 'info' | 'warning' | 'positive' | 'critical' | 'muted';

const TONE_CLASS: Record<Tone, string> = {
    info: styles.pillInfo,
    warning: styles.pillWarning,
    positive: styles.pillPositive,
    critical: styles.pillCritical,
    muted: styles.pillMuted,
};

const STATUS_TONE: Record<string, Tone> = {
    Submitted: 'warning',
    'PI Issued': 'info',
    'PI Confirmed': 'info',
    'Payment Submitted': 'warning',
    'Payment Done': 'positive',
    Shipped: 'positive',
    Fulfilled: 'positive',
    Cancelled: 'muted',
    SUBMITTED: 'warning',
    VERIFIED: 'positive',
    REJECTED: 'critical',
    DRAFT: 'muted',
    APPROVED: 'info',
    DISPATCHED: 'positive',
    IN_TRANSIT: 'positive',
    RECEIVED: 'positive',
    CANCELLED: 'muted',
    ACTIVE: 'positive',
    SUPERSEDED: 'muted',
    CLOSED: 'muted',
};

export const Pill: React.FC<{ label: string; tone?: Tone }> = ({ label, tone }) => (
    <span className={`${styles.pill} ${TONE_CLASS[tone ?? STATUS_TONE[label] ?? 'muted']}`}>{label}</span>
);

export const Card: React.FC<{ title?: string; hint?: string; actions?: React.ReactNode; children?: React.ReactNode }> = ({
    title,
    hint,
    actions,
    children,
}) => (
    <div className={styles.card}>
        {title || actions ? (
            <div className={styles.between}>
                {title ? <span className={styles.cardTitle}>{title}</span> : <span />}
                {actions}
            </div>
        ) : null}
        {hint ? <span className={styles.cardHint}>{hint}</span> : null}
        {children}
    </div>
);

export const Field: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
    <div className={styles.field}>
        <span className={styles.fieldLabel}>{label}</span>
        <span className={styles.fieldValue}>{value ?? '—'}</span>
    </div>
);

export const ProductCell: React.FC<{ name: string; sku?: string | null }> = ({ name, sku }) => (
    <div className={styles.productName}>
        <span>{name}</span>
        {sku && sku !== name ? <span className={styles.productSku}>{sku}</span> : null}
    </div>
);

export const OrderSteps: React.FC<{ status: B2bOrderStatus; labels?: Partial<Record<B2bOrderStatus, string>> }> = ({ status, labels }) => {
    if (status === 'Cancelled') return <Pill label="Cancelled" />;
    const current = ORDER_STEPS.indexOf(status);
    return (
        <div className={styles.steps}>
            {ORDER_STEPS.map((step, index) => (
                <span
                    key={step}
                    className={`${styles.step} ${index < current ? styles.stepDone : ''} ${index === current ? styles.stepCurrent : ''}`}
                >
                    {labels?.[step] ?? step}
                </span>
            ))}
        </div>
    );
};

/**
 * Subtotal, scheme discounts, GST, total and the rounded payable of a quote. Given the quote's lines,
 * the subtotal reads as paid products and free goods separately; a saved order without them shows one line.
 */
type TotalsLine = { quantity: number; lineTotal: number };
export const QuoteTotals: React.FC<{ quote: Pick<OrderQuote, 'charges' | 'totals'> & { lines?: TotalsLine[]; freeLines?: TotalsLine[] } }> = ({ quote }) => {
    const roundOff = Math.round((quote.totals.payable - quote.totals.total) * 100) / 100;
    const paid = quote.lines ? sumLines(quote.lines) : null;
    const free = quote.freeLines?.length ? sumLines(quote.freeLines) : null;
    return (
        <div className={styles.totals}>
            {paid ? (
                <>
                    <div className={styles.totalsRow}>
                        <span>Products ({units(paid.units)} pcs)</span>
                        <span>{money(paid.value)}</span>
                    </div>
                    {free ? (
                        <div className={styles.totalsRow}>
                            <span>Free goods ({units(free.units)} pcs)</span>
                            <span>{money(free.value)}</span>
                        </div>
                    ) : null}
                </>
            ) : (
                <div className={styles.totalsRow}>
                    <span>Subtotal (incl. free goods at list price)</span>
                    <span>{money(quote.totals.subtotal)}</span>
                </div>
            )}
            {quote.charges.map((charge) => (
                <div key={`${charge.label}:${charge.amount}`} className={styles.totalsRow}>
                    <span>{charge.label}</span>
                    <span>{money(charge.amount)}</span>
                </div>
            ))}
            <div className={styles.totalsRow}>
                <span>GST</span>
                <span>{money(quote.totals.taxAmount)}</span>
            </div>
            {roundOff !== 0 ? (
                <div className={styles.totalsRow}>
                    <span>Round off</span>
                    <span>{money(roundOff)}</span>
                </div>
            ) : null}
            <div className={`${styles.totalsRow} ${styles.totalsStrong} ${styles.divider}`}>
                <span>Payable</span>
                <span>{money(quote.totals.payable)}</span>
            </div>
        </div>
    );
};


/**
 * The schemes running for the customer, as one line above the catalogue. A units-based free-goods
 * scheme the order has touched gets a chip: units, how many more earn the next piece (ring), and
 * how many of its free pieces are chosen. Every other scheme folds into "Other schemes".
 */
export const SchemeStrip: React.FC<{
    schemes: B2bScheme[];
    /** The paid lines with their category, as typed (progress updates before the server quote). */
    lines: Array<OrderLineInput & { category: string | null }>;
    entitlements: SchemeEntitlement[];
    picks: OrderLineInput[];
    emptyText: string;
}> = ({ schemes, lines, entitlements, picks, emptyText }) => {
    const [showOthers, setShowOthers] = useState(false);
    if (schemes.length === 0) return <span className={styles.muted}>{emptyText}</span>;
    const chosen = freeChosen(entitlements, picks);
    const touched = schemes.map((s) => ({ s, progress: freeSchemeProgress(s, lines) })).filter((x) => x.progress && x.progress.units > 0);
    const others = schemes.filter((s) => !touched.some((t) => t.s.id === s.id));
    return (
        <div className={styles.schemeStrip}>
            <div className={styles.row}>
                <span className={styles.fieldLabel}>Schemes</span>
                {touched.length === 0 ? <span className={styles.muted}>Add products to see the schemes they earn.</span> : null}
                {touched.map(({ s, progress }) => {
                    const { units: count, toNext } = progress as NonNullable<typeof progress>;
                    const earned = entitlements.find((e) => e.schemeCode === s.code)?.units ?? 0;
                    const done = chosen.get(s.code) ?? 0;
                    const buy = s.rule.slabs[0]?.buy ?? 1;
                    return (
                        <span key={s.id} className={styles.schemeChip} title={`${s.name} · ${describeScheme(s)}`}>
                            <RingProgress size={20} thickness={3} roundCaps sections={[{ value: toNext ? ((buy - toNext) / buy) * 100 : 100, color: 'teal' }]} />
                            <strong>{s.name}</strong>
                            <span className={styles.muted}>
                                {units(count)} units{toNext ? ` · next in ${units(toNext)}` : ''}
                            </span>
                            {earned > 0 ? (
                                <Pill label={`${units(done)} of ${units(earned)} free${done === earned ? ' ✓' : ''}`} tone={done === earned ? 'positive' : 'critical'} />
                            ) : (
                                <Pill label={`${units(toNext)} more`} tone="muted" />
                            )}
                        </span>
                    );
                })}
                {others.length ? (
                    <Button size="xs" variant={ButtonVariant.SUBTLE} onClick={() => setShowOthers((v) => !v)}>
                        {showOthers ? 'Hide' : 'Other schemes'} ({others.length})
                    </Button>
                ) : null}
            </div>
            {showOthers
                ? others.map((s) => (
                      <span key={s.id} className={styles.cardHint}>
                          <strong>{s.name}</strong> · {describeScheme(s)}
                      </span>
                  ))
                : null}
        </div>
    );
};

